import { X } from 'lucide-react';
import * as React from 'react';

import { cn } from '../../lib/utils';

/**
 * ChipInput — tag / alt-SKU chip input Excel paste-tel + createable módusz
 * (STORY-1104, ADR-118 Am.2 tags + ADR-123 alt SKUs, Sally 01-csomag §2.8 +
 * 04-cikk §2.3).
 *
 * Kontrollált komponens (values + onChange). Szülő felelőssége i18n + szerver
 * side deduplikáció. Inline warning toast-ok a szülő toast rendszerét várják.
 *
 * Kulcs viselkedések:
 *  - Enter / Tab → aktuális bemenetből chip
 *  - Backspace üres bemenettel → utolsó chip törlés
 *  - Vessző / tab / newline splitter (Excel paste-kompat)
 *  - Createable: suggestions nincs találat → "+ Új: '<text>'"
 *  - Duplicate védelem: case-insensitive, onDuplicate callback
 *  - Validator hook: minden új chip-et validál, hibát jelez inline
 *  - Suggestions: nyílgombokkal + Enter-rel
 */

export interface ChipInputValidationResult {
  ok: boolean;
  error?: string;
}

export type ChipInputValidator = (value: string) => ChipInputValidationResult | string | true;

export interface ChipInputProps extends Omit<
  React.HTMLAttributes<HTMLDivElement>,
  'onChange' | 'onInvalid'
> {
  values: string[];
  onChange: (values: string[]) => void;
  placeholder?: string;
  createable?: boolean;
  suggestions?: string[];
  validator?: ChipInputValidator;
  disabled?: boolean;
  maxChips?: number;
  /** Called with the duplicate value when user tries to add an existing chip. */
  onDuplicate?: (value: string) => void;
  /** Called with the invalid value + reason when validator rejects. */
  onInvalid?: (value: string, reason: string) => void;
  /** ID for label htmlFor (a11y). */
  id?: string;
  /** Aria-describedby forwarded to the input. */
  'aria-describedby'?: string;
  /** Labels (i18n hook). */
  labels?: ChipInputLabels;
}

export interface ChipInputLabels {
  create?: (value: string) => string;
  duplicate?: string;
  remove?: string;
  maxReached?: string;
  /** Fallback when validator returns `{ ok: false }` without a custom error. */
  invalid?: string;
}

const DEFAULT_LABELS: Required<ChipInputLabels> = {
  create: (v: string) => `+ Új: "${v}"`,
  duplicate: 'Már szerepel',
  remove: 'Eltávolítás',
  maxReached: 'Elérte a maximumot',
  invalid: 'Érvénytelen érték',
};

const SPLITTER_REGEX = /[\n\t,]/;

function normalize(value: string): string {
  return value.trim();
}

function resolveValidation(
  value: string,
  validator?: ChipInputValidator
): ChipInputValidationResult {
  if (!validator) return { ok: true };
  const r = validator(value);
  if (r === true) return { ok: true };
  if (typeof r === 'string') return { ok: false, error: r };
  return r;
}

export function ChipInput({
  values,
  onChange,
  placeholder,
  createable = false,
  suggestions,
  validator,
  disabled,
  maxChips,
  onDuplicate,
  onInvalid,
  labels,
  className,
  id,
  'aria-describedby': ariaDescribedBy,
  ...rest
}: ChipInputProps) {
  const L = { ...DEFAULT_LABELS, ...labels };
  const [draft, setDraft] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);
  const [highlightIdx, setHighlightIdx] = React.useState(-1);
  const inputRef = React.useRef<HTMLInputElement>(null);

  const normalizedValues = React.useMemo(() => values.map(v => v.toLowerCase()), [values]);

  const filteredSuggestions = React.useMemo(() => {
    if (!suggestions || suggestions.length === 0 || draft.length === 0) return [];
    const q = draft.toLowerCase();
    return suggestions
      .filter(s => s.toLowerCase().includes(q) && !normalizedValues.includes(s.toLowerCase()))
      .slice(0, 8);
  }, [suggestions, draft, normalizedValues]);

  const hasExactMatch = suggestions?.some(s => s.toLowerCase() === draft.toLowerCase()) ?? false;

  const showCreateOption =
    createable && draft.length > 0 && !hasExactMatch && suggestions !== undefined;

  const addChip = React.useCallback(
    (raw: string): boolean => {
      const value = normalize(raw);
      if (!value) return false;
      if (maxChips !== undefined && values.length >= maxChips) {
        setError(L.maxReached);
        return false;
      }
      if (normalizedValues.includes(value.toLowerCase())) {
        setError(L.duplicate);
        onDuplicate?.(value);
        return false;
      }
      const v = resolveValidation(value, validator);
      if (!v.ok) {
        setError(v.error ?? L.invalid);
        onInvalid?.(value, v.error ?? L.invalid);
        return false;
      }
      setError(null);
      onChange([...values, value]);
      return true;
    },
    [
      maxChips,
      values,
      normalizedValues,
      onChange,
      validator,
      onDuplicate,
      onInvalid,
      L.duplicate,
      L.maxReached,
    ]
  );

  const addChipsBulk = React.useCallback(
    (raws: string[]): number => {
      const accepted: string[] = [];
      const seen = new Set(normalizedValues);
      let lastError: string | null = null;
      for (const raw of raws) {
        const value = normalize(raw);
        if (!value) continue;
        if (maxChips !== undefined && values.length + accepted.length >= maxChips) {
          lastError = L.maxReached;
          break;
        }
        if (seen.has(value.toLowerCase())) {
          lastError = L.duplicate;
          onDuplicate?.(value);
          continue;
        }
        const v = resolveValidation(value, validator);
        if (!v.ok) {
          lastError = v.error ?? L.invalid;
          onInvalid?.(value, v.error ?? L.invalid);
          continue;
        }
        accepted.push(value);
        seen.add(value.toLowerCase());
      }
      if (accepted.length > 0) {
        setError(lastError);
        onChange([...values, ...accepted]);
      } else if (lastError) {
        setError(lastError);
      }
      return accepted.length;
    },
    [
      maxChips,
      values,
      normalizedValues,
      onChange,
      validator,
      onDuplicate,
      onInvalid,
      L.duplicate,
      L.maxReached,
    ]
  );

  const removeChip = (idx: number) => {
    if (disabled) return;
    const next = values.filter((_, i) => i !== idx);
    onChange(next);
  };

  const flushDraft = React.useCallback(() => {
    if (draft.length === 0) return;
    if (addChip(draft)) {
      setDraft('');
      setHighlightIdx(-1);
    }
  }, [draft, addChip]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === 'Tab') {
      if (highlightIdx >= 0 && filteredSuggestions[highlightIdx]) {
        e.preventDefault();
        if (addChip(filteredSuggestions[highlightIdx])) {
          setDraft('');
          setHighlightIdx(-1);
        }
        return;
      }
      if (draft.length > 0) {
        e.preventDefault();
        flushDraft();
      }
      return;
    }
    if (e.key === 'Backspace' && draft.length === 0 && values.length > 0) {
      e.preventDefault();
      removeChip(values.length - 1);
      return;
    }
    if (e.key === 'ArrowDown' && filteredSuggestions.length > 0) {
      e.preventDefault();
      setHighlightIdx(prev => (prev + 1) % filteredSuggestions.length);
      return;
    }
    if (e.key === 'ArrowUp' && filteredSuggestions.length > 0) {
      e.preventDefault();
      setHighlightIdx(prev => (prev <= 0 ? filteredSuggestions.length - 1 : prev - 1));
      return;
    }
    if (e.key === 'Escape') {
      setHighlightIdx(-1);
      setDraft('');
      setError(null);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    setError(null);
    if (SPLITTER_REGEX.test(v)) {
      const parts = v.split(SPLITTER_REGEX);
      addChipsBulk(parts);
      setDraft('');
      setHighlightIdx(-1);
      return;
    }
    setDraft(v);
    setHighlightIdx(-1);
  };

  const handleBlur = () => {
    if (draft.length > 0) flushDraft();
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const text = e.clipboardData.getData('text');
    if (!text) return;
    if (SPLITTER_REGEX.test(text)) {
      e.preventDefault();
      addChipsBulk(text.split(SPLITTER_REGEX));
      setDraft('');
    }
  };

  const dropdownOpen = filteredSuggestions.length > 0 || showCreateOption;

  return (
    <div
      data-testid="chip-input"
      data-disabled={disabled ? 'true' : undefined}
      className={cn(
        'relative flex flex-col gap-1 rounded-md border border-input bg-background px-2 py-1.5',
        'focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-0',
        disabled && 'cursor-not-allowed opacity-60',
        error && 'border-red-500',
        className
      )}
      {...rest}
    >
      <div className="flex flex-wrap items-center gap-1">
        {values.map((v, idx) => (
          <span
            key={`${v}-${idx}`}
            data-testid="chip"
            data-value={v}
            className={cn(
              'inline-flex items-center gap-1 rounded-full bg-secondary px-2 py-0.5 text-xs text-secondary-foreground'
            )}
          >
            {v}
            {!disabled && (
              <button
                type="button"
                aria-label={`${L.remove}: ${v}`}
                onClick={() => removeChip(idx)}
                className="rounded-full outline-none ring-offset-background hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring"
              >
                <X size={12} aria-hidden="true" />
              </button>
            )}
          </span>
        ))}
        <input
          ref={inputRef}
          id={id}
          type="text"
          value={draft}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          onPaste={handlePaste}
          disabled={disabled}
          placeholder={values.length === 0 ? placeholder : undefined}
          aria-describedby={ariaDescribedBy}
          aria-invalid={error ? 'true' : undefined}
          className="flex-1 border-0 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
        />
      </div>

      {error && (
        <p role="alert" data-testid="chip-input-error" className="text-xs text-red-600">
          {error}
        </p>
      )}

      {dropdownOpen && (
        <ul
          role="listbox"
          data-testid="chip-input-suggestions"
          className="absolute left-0 top-full z-10 mt-1 max-h-48 w-full overflow-auto rounded-md border border-border bg-popover p-1 shadow-md"
        >
          {filteredSuggestions.map((s, i) => (
            <li
              key={s}
              role="option"
              aria-selected={i === highlightIdx}
              data-testid="chip-input-suggestion"
              onMouseDown={e => {
                e.preventDefault();
                if (addChip(s)) setDraft('');
              }}
              className={cn(
                'cursor-pointer rounded px-2 py-1 text-sm',
                i === highlightIdx && 'bg-accent text-accent-foreground'
              )}
            >
              {s}
            </li>
          ))}
          {showCreateOption && (
            <li
              role="option"
              aria-selected={false}
              data-testid="chip-input-create-option"
              onMouseDown={e => {
                e.preventDefault();
                flushDraft();
              }}
              className="cursor-pointer rounded px-2 py-1 text-sm text-primary"
            >
              {L.create(draft)}
            </li>
          )}
        </ul>
      )}
    </div>
  );
}
